import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
} from "remotion";
import { COLORS, FONTS, CTA_GOLD, BADGE_DARK, GLOBS } from "./theme";
import { Background } from "./Background";

export const ReelsTikTok: React.FC<{
  hookText: string;
  mainText: string;
  revealText: string;
  cta: string;
}> = ({ hookText, mainText, revealText, cta }) => {
  const frame = useCurrentFrame();

  const hookOpacity = interpolate(frame, [0, 15, 85, 100], [0, 1, 1, 0]);
  const hookScale = spring({ frame: Math.min(frame, 85), fps: 30, config: { damping: 12 } });

  const mainOpacity = interpolate(frame, [100, 115, 185, 200], [0, 1, 1, 0]);
  const mainSlide = interpolate(frame, [100, 120], [60, 0], { extrapolateLeft: "clamp" });

  const revealOpacity = interpolate(frame, [200, 215], [0, 1]);
  const revealScale = spring({ frame: Math.max(frame - 200, 0), fps: 30, config: { damping: 10 } });

  const ctaOpacity = interpolate(frame, [250, 270], [0, 1]);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(160deg, ${COLORS.dark} 0%, ${COLORS.darkAlt} 100%)`,
        fontFamily: FONTS.sans,
      }}
    >
      <Background theme="dark" />

      <div
        style={{
          position: "absolute",
          top: -150,
          right: -100,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: GLOBS.goldGlow,
          filter: "blur(80px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -200,
          left: -100,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: GLOBS.darkGlow,
          filter: "blur(100px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 40,
          top: "40%",
          width: 250,
          height: 250,
          borderRadius: "50%",
          border: "1px solid rgba(197,160,89,0.08)",
          opacity: 0.3,
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 40,
          left: 40,
          display: "flex",
          alignItems: "center",
          gap: 8,
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: COLORS.gold,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 700,
            color: COLORS.dark,
            fontFamily: FONTS.serif,
            paddingBottom: 2,
            boxShadow: `0 2px 12px ${COLORS.gold}40`,
          }}
        >
          E
        </div>
        <span
          style={{
            fontSize: 11,
            color: COLORS.textMuted,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          @santoro.analista
        </span>
      </div>

      <div
        style={{
          position: "absolute",
          top: 90,
          left: 40,
          zIndex: 2,
          opacity: hookOpacity,
        }}
      >
        <span style={BADGE_DARK}>VOCÊ SABIA?</span>
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 60,
        }}
      >
        <div
          style={{
            transform: `scale(${hookScale})`,
            opacity: hookOpacity,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: FONTS.serif,
              fontSize: 72,
              fontWeight: 400,
              color: COLORS.white,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              textShadow: "0 4px 20px rgba(0,0,0,0.5)",
            }}
          >
            {hookText}
          </div>
        </div>

        <div
          style={{
            opacity: mainOpacity,
            transform: `translateY(${mainSlide}px)`,
            textAlign: "center",
            position: "absolute",
            padding: 60,
          }}
        >
          <div
            style={{
              fontSize: 36,
              color: COLORS.textMuted,
              lineHeight: 1.5,
              fontWeight: 300,
              maxWidth: "80%",
              margin: "0 auto",
              textShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            {mainText}
          </div>
        </div>

        <div
          style={{
            opacity: revealOpacity,
            transform: `scale(${revealScale})`,
            textAlign: "center",
            position: "absolute",
            padding: 60,
          }}
        >
          <div
            style={{
              fontFamily: FONTS.serif,
              fontSize: 64,
              fontWeight: 400,
              color: COLORS.gold,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              textShadow: `0 2px 20px ${COLORS.gold}30`,
            }}
          >
            {revealText}
          </div>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          opacity: ctaOpacity,
          zIndex: 2,
        }}
      >
        <div
          style={{
            ...CTA_GOLD,
            padding: "16px 48px",
            fontSize: 16,
          }}
        >
          {cta}
        </div>
      </div>
    </AbsoluteFill>
  );
};
